// native
import { Component, ElementRef, HostListener, Input, OnDestroy, OnInit } from '@angular/core';
import { Subject, Subscription } from 'rxjs';
import { debounceTime } from 'rxjs/operators';

// service
import { BaseFilterCellComponent, GridFilterService } from '../grid-filter.service';

// models
import { FilterOperator } from 'app/models';

const OPERATOR_LABELS: Record<string, string> = {
  contains: 'Contains',
  eq: 'Is equal to',
  startswith: 'Starts with',
  endswith: 'Ends with',
  gte: 'Is after or equal to',
  lte: 'Is before or equal to'
};

/** Text filter cell: input + operator chooser + clear button. */
@Component({
    selector: 'app-grid-string-filter',
    templateUrl: './grid-string-filter.component.html',
    standalone: false
})
export class GridStringFilterComponent extends BaseFilterCellComponent implements OnInit, OnDestroy {
  @Input() column: { field: string; };
  @Input() operators: FilterOperator[] = ['contains', 'eq', 'startswith', 'endswith'];

  value = '';
  selectedOperator: FilterOperator;
  menuOpen = false;

  private readonly input$ = new Subject<void>();
  private sub: Subscription;

  constructor(filterService: GridFilterService, private host: ElementRef) {
    super(filterService);
  }

  ngOnInit(): void {
    const existing = this.filterByField(this.column.field);
    this.value = (existing?.value as string) ?? '';
    this.selectedOperator = (existing?.operator as FilterOperator) ?? this.operators[0];
    // mirrors kendo's 500ms filter input debounce
    this.sub = this.input$.pipe(debounceTime(500)).subscribe(() => this.apply());
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
  }

  onInput(value: string): void {
    this.value = value;
    this.input$.next();
  }

  toggleMenu(): void {
    this.menuOpen = !this.menuOpen;
  }

  selectOperator(op: FilterOperator): void {
    this.selectedOperator = op;
    this.menuOpen = false;
    if (this.value !== '') {
      this.apply();
    }
  }

  clear(): void {
    this.value = '';
    this.apply();
  }

  label(op: string): string {
    return OPERATOR_LABELS[op] ?? op;
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (this.menuOpen && !this.host.nativeElement.contains(event.target)) {
      this.menuOpen = false;
    }
  }

  private apply(): void {
    this.applyFilter(
      this.value === null || this.value === ''
        ? this.removeFilter(this.column.field)
        : this.updateFilter({ field: this.column.field, operator: this.selectedOperator, value: this.value })
    );
  }
}
